import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

import { useApp } from 'contexts/AppContext';
import { useTheme } from 'contexts/ThemeContext';

import {
  countMarkedForDelete,
  formatListFieldUser,
  getKeptListFieldValues,
  parseCustomLabel
} from 'utils/functions';
import pluralize from 'utils/pluralize';
import { FUNNEL, USERS } from 'utils/rest';

import { HelpTooltip, Text, Tooltip } from 'components/DataDisplay';
import { Modal } from 'components/Feedback';
import { FormSection } from 'components/FormSection';
import {
  AutocompleteTextField,
  Button,
  MultipleSelect,
  Select,
  Switch,
  TextField
} from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { ListField } from 'components/ListField';
import { Loading } from 'components/Loading';

import {
  ACTIVATION_REQUIRED_FIELD_OPTIONS,
  formatActivationRequiredMediaConfigs,
  mapActivationRequiredMediaConfigsFromConfig,
  normalizeActivationRequiredMediaConfigs
} from '../../../DomainContainer/utils';

const ErrorState = {
  errorName: false,
  errorPreset: false
};

const budgetTypeImpressionsOptions = [
  { label: 'Comme le domaine', value: 'domain' },
  { label: 'Impressions', value: 'impressions' },
  { label: 'Budget', value: 'budget' }
];

export const ModalForm = props => {
  const { domainConfig } = useApp();
  const { themeColors } = useTheme();
  const { config, onNext, onClose, activableMedias } = props;
  const {
    activationPresets = [],
    insightUserTarget = [],
    activatedAudiences = [],
    actualState = {},
    id,
    importModel,
    connectorsConnexion,
    stripeCustomers,
    canEditCustomer,
    dataProvidersOptions = []
  } = config;
  const initialState = {
    name: '',
    presetId: '',
    crmPresetId: '',
    selectedUsers: [],
    selectedFunnels: [],
    selectedActivationPresets: [],
    selectedInsightTarget: [],
    selectedActivatedAudiences: [],
    selectedPlatformAccounts: [],
    selectedPlatformPages: [],
    selectedAccountManagers: [],
    useFunnelDataInActivation: false,
    enableCRMCampaign: false,
    customerName: '',
    key: '',
    stripeCustomerId: '',
    dataProviders: [],
    excludeDataProviders: [],
    copyPal: domainConfig?.features?.copyPal ?? false,
    selectedActivableMedias: [],
    activationRequiredMediaConfigs: [],
    selectedBudgetTypeImpressions: 'domain'
  };

  const [
    {
      name,
      presetId,
      crmPresetId,
      selectedUsers,
      selectedFunnels,
      selectedActivationPresets,
      selectedInsightTarget,
      selectedActivatedAudiences,
      selectedPlatformAccounts,
      selectedPlatformPages,
      selectedAccountManagers,
      useFunnelDataInActivation,
      enableCRMCampaign,
      key,
      stripeCustomerId,
      dataProviders,
      excludeDataProviders,
      copyPal,
      selectedActivableMedias,
      activationRequiredMediaConfigs,
      selectedBudgetTypeImpressions
    },
    setState
  ] = useState({
    ...initialState,
    ...actualState,
    activationRequiredMediaConfigs: mapActivationRequiredMediaConfigsFromConfig(
      actualState?.activationRequiredMediaConfigs || []
    ),
    selectedBudgetTypeImpressions:
      actualState?.budgetTypeImpressions || 'domain'
  });

  const [users, setUsers] = useState([]);
  const [funnels, setFunnels] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const timerRef = useRef(null);
  const timerRefFunnels = useRef(null);

  const [{ errorName, errorPreset }, setError] = useState(ErrorState);
  const ownPreset = id
    ? activationPresets.find(preset => preset.name === actualState.name)
    : undefined;

  const toggleErrorName = state => {
    setError(prevState => ({
      ...prevState,
      errorName: state
    }));
  };

  const toggleErrorPreset = state => {
    setError(prevState => ({
      ...prevState,
      errorPreset: state
    }));
  };

  const handleChange = field => value => {
    const newValue = typeof value === 'string' ? value.trim() : value;
    if (field === 'name') {
      toggleErrorName(!newValue);
    }
    if (field === 'crmPresetId' && enableCRMCampaign) {
      toggleErrorPreset(!newValue);
    }
    setState(prevState => ({
      ...prevState,
      [field]: newValue
    }));
  };

  const handleChangeFunnels = value => {
    setState(prevState => ({
      ...prevState,
      selectedFunnels: value
    }));
  };

  const handleChangePlatformAccount = value => {
    setState(prevState => ({
      ...prevState,
      selectedPlatformAccounts: value.map(el => {
        let { connexionId } = el;
        if (typeof el.connexionId === 'string') {
          connexionId = {
            value: el.connexionId,
            label: connectorsConnexion.find(e => e.value === el.connexionId)
              ?.label
          };
        }
        return {
          ...el,
          connexionId
        };
      })
    }));
  };

  const handleChangePlatformPage = value => {
    setState(prevState => ({
      ...prevState,
      selectedPlatformPages: value.map(el => {
        let { connexionId } = el;
        if (typeof el.connexionId === 'string') {
          connexionId = {
            value: el.connexionId,
            label: connectorsConnexion.find(e => e.value === el.connexionId)
              ?.label
          };
        }
        return {
          ...el,
          connexionId
        };
      })
    }));
  };

  const resetStates = () => {
    setState(initialState);
    setError(ErrorState);
  };

  const flattenDataProviders = providers => {
    return providers.map(({ key: provider }) => provider);
  };

  const arrayAreIdenticals = (arr1 = [], arr2 = []) => {
    if (arr1.length < arr2.length) {
      return false;
    }
    return arr2.every(el => arr1.includes(el));
  };

  const getActivationRequiredMediaOptions = () => {
    const activeMediaKeys =
      Array.isArray(selectedActivableMedias) &&
      selectedActivableMedias.length > 0
        ? selectedActivableMedias
        : domainConfig?.activableMedias || [];

    return activableMedias.reduce((acc, media) => {
      if (
        !activeMediaKeys.includes(media.key) ||
        !(media.page || media.canSetUp)
      ) {
        return acc;
      }

      acc.push({
        value: media.key,
        label: media.name
      });

      return acc;
    }, []);
  };

  const activationRequiredMediaConfigsAreIdenticals = (
    values = [],
    comparedValues = []
  ) => {
    const normalizedValues = normalizeActivationRequiredMediaConfigs(values);
    const normalizedComparedValues = normalizeActivationRequiredMediaConfigs(
      comparedValues
    );
    const isIdentical =
      JSON.stringify(normalizedValues) ===
      JSON.stringify(normalizedComparedValues);

    return isIdentical;
  };

  const onValidate = async () => {
    let error = false;
    const groupPresetId = presetId || ownPreset?._id;
    const isEmptyDataProviders = dataProviders.length === 0;
    const isEmptyExcludeDataProviders = excludeDataProviders.length === 0;
    const datas = {
      name,
      selectedUsers: getKeptListFieldValues(
        selectedUsers,
        actualState?.userIds
      ),
      selectedFunnels: getKeptListFieldValues(
        selectedFunnels,
        actualState?.funnelIds
      ),
      selectedActivationPresets: getKeptListFieldValues(
        selectedActivationPresets,
        actualState?.activationPresetIds
      ),
      selectedInsightTarget: getKeptListFieldValues(
        selectedInsightTarget,
        actualState?.insightUserTarget
      ),
      selectedActivatedAudiences: getKeptListFieldValues(
        selectedActivatedAudiences,
        actualState?.activatedAudiences
      ),
      selectedPlatformAccounts: getKeptListFieldValues(
        selectedPlatformAccounts
      ),
      selectedPlatformPages: getKeptListFieldValues(selectedPlatformPages),
      selectedAccountManagers: getKeptListFieldValues(
        selectedAccountManagers,
        actualState?.accountManagers
      ),
      presetId: groupPresetId || '',
      crmPresetId: crmPresetId || groupPresetId,
      stripeCustomerId,
      useFunnelDataInActivation,
      enableCRMCampaign,
      dataProviders: isEmptyDataProviders
        ? dataProviders
        : flattenDataProviders(dataProviders),
      excludeDataProviders: isEmptyExcludeDataProviders
        ? excludeDataProviders
        : flattenDataProviders(excludeDataProviders),
      budgetTypeImpressions: selectedBudgetTypeImpressions
    };
    // On ne set la variable que si la valeur diffère de celle du domain
    if (
      config?.actualState?.copyPal !== undefined ||
      copyPal !== domainConfig?.features?.copyPal
    ) {
      datas.copyPal = copyPal;
    }

    // On ne set la variable que si la valeur diffère de celle du domain
    if (
      actualState.activableMedias?.length !== 0 ||
      !arrayAreIdenticals(
        selectedActivableMedias,
        domainConfig?.activableMedias
      )
    ) {
      datas.activableMedias = selectedActivableMedias;
    }

    const formattedActivationRequiredMediaConfigs = formatActivationRequiredMediaConfigs(
      getKeptListFieldValues(activationRequiredMediaConfigs)
    );

    if (
      actualState.activationRequiredMediaConfigs?.length !== 0 ||
      !activationRequiredMediaConfigsAreIdenticals(
        formattedActivationRequiredMediaConfigs,
        domainConfig?.activationRequiredMediaConfigs || []
      )
    ) {
      datas.activationRequiredMediaConfigs = formattedActivationRequiredMediaConfigs;
    }
    if (!name) {
      toggleErrorName(true);
      error = true;
    }
    if (enableCRMCampaign && !crmPresetId) {
      toggleErrorPreset(true);
      error = true;
    }

    if (!error) {
      onNext(datas);
      resetStates();
    }
  };

  const getActions = () => {
    const markedCount = countMarkedForDelete([
      selectedUsers,
      selectedAccountManagers,
      selectedFunnels,
      selectedActivationPresets,
      selectedInsightTarget,
      selectedActivatedAudiences,
      selectedPlatformAccounts,
      selectedPlatformPages,
      activationRequiredMediaConfigs
    ]);
    return (
      <Container>
        <Row spacing={1}>
          <Item xs justify="flex-end">
            {markedCount > 0 && (
              <Text
                color={themeColors.designSystem.red}
                style={{ paddingRight: '16px' }}
              >
                {markedCount} {pluralize('élément', markedCount)} à supprimer
              </Text>
            )}
            {selectedActivableMedias.length > 0 &&
              !arrayAreIdenticals(
                selectedActivableMedias,
                domainConfig?.activableMedias
              ) && (
                <Tooltip
                  isIcon
                  title="Vous êtes sur le point de retirer des médias activables. Veuillez noter que ces médias ne pourront plus être utilisés pour vos campagnes marketing une fois supprimés."
                >
                  <WarningRoundedIcon
                    sx={{
                      color: themeColors.themeV2.notValidated,
                      paddingRight: '10px'
                    }}
                  />
                </Tooltip>
              )}
            <Button
              variant="contained"
              size="medium"
              widthSize="medium"
              onClick={onValidate}
            >
              {actualState ? 'Actualiser' : 'Créer'} l&lsquo;organisation
            </Button>
          </Item>
        </Row>
      </Container>
    );
  };

  const onAutoCompliteChange = field => data => {
    setState(prevState => ({
      ...prevState,
      [field]: data?._id
    }));
  };

  const fetchUsers = async (search, adminOnly) => {
    const body = { search };
    if (adminOnly) {
      body.roles = ['ADMIN'];
    }
    const found = await USERS.getPaginated(body);
    if (found?.result) {
      if (adminOnly) {
        setAdminUsers(found.result);
      } else {
        setUsers(found.result);
      }
    }
  };

  const pollUsers = (query, adminOnly) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      fetchUsers(query, adminOnly);
    }, 500);
  };

  const fetchFunnels = async search => {
    const body = { search };
    const found = await FUNNEL.getPaginated(body);
    if (found?.data) {
      setFunnels(found.data);
    }
  };

  const pollFunnels = query => {
    if (timerRefFunnels.current) {
      clearTimeout(timerRefFunnels.current);
    }
    timerRefFunnels.current = setTimeout(() => {
      fetchFunnels(query, 1, 20);
    }, 500);
  };

  const getVisibilityIcon = item => {
    return !item.archived ? (
      <VisibilityIcon style={{ color: themeColors.light }} />
    ) : (
      <VisibilityOffIcon style={{ color: themeColors.light }} />
    );
  };

  const loadData = async () => {
    const funnelIds = actualState?.funnelIds || [];

    const groupFunnels = funnelIds?.length
      ? await FUNNEL.getFunnelsByIds(funnelIds)
      : [];

    await Promise.all([fetchUsers(''), fetchUsers('', true), fetchFunnels('')]);

    setState(prev => {
      const item = { ...prev };
      item.selectedFunnels = funnelIds.reduce((acc, el) => {
        const found = groupFunnels.find(funnel => funnel._id === el);
        if (found) {
          acc.push({
            name: found.name,
            _id: found._id,
            visibility: getVisibilityIcon(found)
          });
        }

        return acc;
      }, []);

      return item;
    });

    setLoaded(true);
  };

  useEffect(() => {
    if (!loaded) {
      loadData();
    }
    // eslint-disable-next-line
  }, []);

  return (
    <Modal
      actions={getActions()}
      {...props}
      onClose={() => {
        resetStates();
        onClose();
      }}
    >
      <Container>
        <Loading loading={!loaded}>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Général" xs={3}>
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item xs={6} justify="flex-start">
                      <Container>
                        <Row spacing={0} justify="flex-start">
                          <Item xs={11} justify="flex-start">
                            <Text color={errorName ? 'error' : 'inherit'}>
                              Nom de l&lsquo;organisation *
                            </Text>
                            <TextField
                              small
                              label="Choisisez un nom pour votre organisation"
                              onChange={handleChange('name')}
                              value={name}
                              error={errorName}
                            />
                          </Item>
                        </Row>
                      </Container>
                    </Item>
                    <Item xs={6} justify="flex-start">
                      <Container>
                        <Row spacing={0} justify="flex-start">
                          <Item xs={11} justify="flex-start">
                            <AutocompleteTextField
                              title="Preset activation par défaut"
                              placeholder="Choisisez un preset activation pour votre organisation"
                              options={activationPresets}
                              formatLabel={option => {
                                return `${option.name} (${option._id})`;
                              }}
                              small
                              value={activationPresets.find(
                                el => el._id === presetId
                              )}
                              onChange={onAutoCompliteChange('presetId')}
                            />
                            {/* <Select
                            small
                            options={activationPresets.map(e => ({
                              label: e.name,
                              value: e._id
                            }))}
                            onChange={handleChange('presetId')}
                            value={presetId}
                          /> */}
                          </Item>
                        </Row>
                      </Container>
                    </Item>
                  </Row>
                  <Row spacing={0} justify="flex-start">
                    <Item xs={6} justify="flex-start">
                      <Container>
                        <Row spacing={0} justify="flex-start">
                          <Item xs={11} justify="flex-start">
                            <AutocompleteTextField
                              title="Compte client"
                              options={stripeCustomers}
                              formatLabel={option => option?.info?.name || ''}
                              disabled={
                                !canEditCustomer || domainConfig.createCustomers
                              }
                              small
                              value={stripeCustomers.find(
                                el => el._id === stripeCustomerId
                              )}
                              onChange={value => {
                                handleChange('stripeCustomerId')(value._id);
                              }}
                            />
                          </Item>
                        </Row>
                      </Container>
                    </Item>
                    <Item xs={6} justify="flex-start">
                      <Container>
                        <Row spacing={0} justify="flex-start">
                          <Item xs={11} justify="flex-start">
                            <TextField
                              small
                              title="Key du magasin / groupe de magasins"
                              disabled
                              value={parseCustomLabel({ key }, ' - ', ['key'])}
                              error={errorName}
                            />
                          </Item>
                        </Row>
                      </Container>
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Sources de données" xs={3}>
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item xs={6} justify="flex-start">
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <Container>
                            <Item justify="flex-start">
                              <MultipleSelect
                                title="Inclure"
                                placeHolder="Toutes les sources de données"
                                small
                                selected={dataProviders.map(el => el.key)}
                                options={dataProvidersOptions}
                                onChange={value => {
                                  handleChange('dataProviders')(value);
                                }}
                              />
                            </Item>
                          </Container>
                        </Item>
                      </Row>
                    </Item>
                    <Item xs={6} justify="flex-start">
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <Container>
                            <Item justify="flex-start">
                              <MultipleSelect
                                title="Exclure"
                                placeHolder="Aucune source de données"
                                small
                                selected={excludeDataProviders.map(
                                  el => el.key
                                )}
                                options={dataProvidersOptions}
                                onChange={value => {
                                  handleChange('excludeDataProviders')(value);
                                }}
                              />
                            </Item>
                          </Container>
                        </Item>
                      </Row>
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Médias activables" xs={3}>
                <Row spacing={0} justify="flex-start">
                  <Item xs={6} justify="flex-start">
                    <Item xs={11} justify="flex-start">
                      <MultipleSelect
                        small
                        required
                        selectAll
                        placeHolder="Comme le domaine"
                        options={activableMedias.map(el => {
                          return {
                            label: el.name,
                            key: el.key
                          };
                        })}
                        selected={selectedActivableMedias}
                        onChange={value => {
                          handleChange('selectedActivableMedias')(
                            value.map(el => el.key)
                          );
                        }}
                      />
                    </Item>
                  </Item>
                </Row>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection
                title="Compte pub requis activation / emailing"
                xs={3}
              >
                <Container>
                  {activationRequiredMediaConfigs.length > 0 && (
                    <Row spacing={0} justify="space-between">
                      <Item
                        xs={3}
                        justify="flex-start"
                        style={{
                          paddingLeft: '20px'
                        }}
                      >
                        <Text bold>Média</Text>
                      </Item>
                      <Item
                        xs={4}
                        justify="flex-start"
                        style={{ transform: 'translateX(-100px)' }}
                      >
                        <Row
                          spacing={0}
                          justify="flex-start"
                          alignItems="center"
                          style={{ flexWrap: 'nowrap' }}
                        >
                          <Item justify="flex-start">
                            <Text bold noWrap>
                              Compte pub/page requis activation
                            </Text>
                          </Item>
                          <Item justify="flex-start">
                            <HelpTooltip
                              title="Combiné au droit du profil une section apparaît pour renseigner les champs dans activation"
                              style={{
                                paddingLeft: '10px'
                              }}
                            />
                          </Item>
                        </Row>
                      </Item>
                      <Item
                        xs={4}
                        justify="flex-start"
                        style={{ transform: 'translateX(-90px)' }}
                      >
                        <Row
                          spacing={0}
                          justify="flex-start"
                          alignItems="center"
                          style={{ flexWrap: 'nowrap' }}
                        >
                          <Item justify="flex-start">
                            <Text bold noWrap>
                              Email tuto ajout compte
                            </Text>
                          </Item>
                          <Item justify="flex-start">
                            <HelpTooltip
                              title="Un email de tutoriel est envoyé au passage de la campagne à prête ou validée pour ajouter le compte pub."
                              style={{
                                paddingLeft: '10px'
                              }}
                            />
                          </Item>
                        </Row>
                      </Item>
                    </Row>
                  )}
                  <Row spacing={0} justify="flex-start">
                    <Item justify="flex-start" xs={11}>
                      <ListField
                        values={activationRequiredMediaConfigs}
                        onChange={handleChange(
                          'activationRequiredMediaConfigs'
                        )}
                        actionTxt="Ajouter un média"
                        deleteAction
                        markDelete
                        variant="customFields"
                        valueUnique
                        labels={[
                          'Média',
                          'Compte pub/page requis',
                          'Email tuto ajout compte'
                        ]}
                        options={['media', 'requiredFields', 'tuto']}
                        inputs={['Select', 'MultiSelect', 'Switch']}
                        fieldsFormat={[
                          getActivationRequiredMediaOptions(),
                          ACTIVATION_REQUIRED_FIELD_OPTIONS,
                          ['Désactivé', 'Activé']
                        ]}
                      />
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Campagne PAL" xs={3}>
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item xs={6} justify="flex-start">
                      <Text>Dupliquer les campagnes PAL au sein du réseau</Text>
                      <HelpTooltip
                        title="Si activé, toutes les campagnes PAL d’un réseau seront présentes dans tous les shops de ce réseau"
                        style={{
                          paddingLeft: '10px'
                        }}
                      />
                    </Item>
                    <Item xs={6} justify="flex-start">
                      <Switch
                        checked={copyPal}
                        onChange={handleChange('copyPal')}
                      />
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Campagne CRM" xs={3}>
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item xs={6} justify="flex-start">
                      <Text>Activer campagne CRM</Text>
                    </Item>
                    <Item xs={6} justify="flex-start">
                      <Switch
                        checked={enableCRMCampaign}
                        onChange={handleChange('enableCRMCampaign')}
                      />
                    </Item>
                  </Row>
                  {enableCRMCampaign && (
                    <Row spacing={0} justify="flex-start">
                      <Item xs={6} justify="flex-start">
                        <AutocompleteTextField
                          title="Sélectionner un preset CRM"
                          placeholder="Choisisez un preset CRM"
                          options={activationPresets}
                          formatLabel={option => {
                            return `${option.name} (${option._id})`;
                          }}
                          small
                          value={activationPresets.find(
                            el => el._id === crmPresetId
                          )}
                          onChange={onAutoCompliteChange('crmPresetId')}
                          error={errorPreset}
                        />
                      </Item>
                    </Row>
                  )}
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Utilisateur" xs={3}>
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item justify="flex-start">
                      <ListField
                        options={users.map(formatListFieldUser)}
                        searchOptionsKeys={['email']}
                        hideKeys={['_id']}
                        values={selectedUsers}
                        onChange={handleChange('selectedUsers')}
                        actionTxt="Ajouter un utilisateur"
                        deleteAction
                        markDelete
                        variant="asyncAutoComplete"
                        onChangeInput={value => {
                          pollUsers(value);
                        }}
                      />
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Funnels" xs={3}>
                <Container>
                  {id && (
                    <Row spacing={0} justify="flex-start">
                      <Item xs={6} justify="flex-start">
                        <Text>Utiliser les données de mes funnels</Text>
                      </Item>
                      <Item xs={6} justify="flex-start">
                        <Switch
                          checked={useFunnelDataInActivation}
                          onChange={handleChange('useFunnelDataInActivation')}
                        />
                      </Item>
                    </Row>
                  )}
                  <Row spacing={0} justify="flex-start">
                    <Item justify="flex-start">
                      <ListField
                        options={funnels.map(e => ({
                          name: e.name,
                          _id: e._id,
                          visibility: getVisibilityIcon(e)
                        }))}
                        searchOptionsKeys={['name', '_id']}
                        hideKeys={['_id']}
                        values={selectedFunnels}
                        onChange={handleChangeFunnels}
                        actionTxt="Ajouter un Funnel"
                        deleteAction
                        markDelete
                        variant="asyncAutoComplete"
                        onChangeInput={value => {
                          pollFunnels(value);
                        }}
                      />
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Paramètres activation" xs={3}>
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item justify="flex-start">
                      <ListField
                        options={activationPresets.map(e => ({
                          name: e.name,
                          _id: e._id
                        }))}
                        searchOptionsKeys={['name', '_id']}
                        values={selectedActivationPresets}
                        onChange={handleChange('selectedActivationPresets')}
                        actionTxt="Ajouter un Paramètre"
                        deleteAction
                        markDelete
                      />
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Sauvegardes Audiences" xs={3}>
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item justify="flex-start">
                      <ListField
                        options={insightUserTarget.map(e => ({
                          name: e.name,
                          _id: e._id
                        }))}
                        searchOptionsKeys={['name', '_id']}
                        values={selectedInsightTarget}
                        onChange={handleChange('selectedInsightTarget')}
                        actionTxt="Ajouter une sauvegarde"
                        deleteAction
                        markDelete
                      />
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Mes audiences activée" xs={3}>
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item justify="flex-start">
                      <ListField
                        options={activatedAudiences.map(e => ({
                          name: e.name,
                          _id: e._id
                        }))}
                        searchOptionsKeys={['name', '_id']}
                        values={selectedActivatedAudiences}
                        onChange={handleChange('selectedActivatedAudiences')}
                        actionTxt="Ajouter une audience"
                        deleteAction
                        markDelete
                      />
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Mes comptes publicitaires" xs={3}>
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item justify="flex-start">
                      <ListField
                        values={selectedPlatformAccounts}
                        onChange={handleChangePlatformAccount}
                        actionTxt="Ajouter un compte"
                        deleteAction
                        markDelete
                        variant="customFields"
                        isRequireds={[
                          true,
                          true,
                          true,
                          true,
                          true,
                          false,
                          false
                        ]}
                        inputs={[
                          'TextField',
                          'Select',
                          'Select',
                          'TextField',
                          'Select',
                          'Select',
                          'TextField'
                        ]}
                        labels={[
                          'Nom',
                          'Nom de la plateforme',
                          'Connexion',
                          'External ID',
                          'Statut',
                          'Connexion Lié (facultatif)',
                          'External ID Lié (facultatif)'
                        ]}
                        options={[
                          'name',
                          'model',
                          'connexionId',
                          'externalId',
                          'status',
                          'linkConnexionId',
                          'linkExternalId'
                        ]}
                        fieldsFormat={[
                          '',
                          importModel,
                          connectorsConnexion,
                          '',
                          [
                            {
                              label: 'Validé',
                              value: 'validated'
                            },
                            {
                              label: 'En cours de validation',
                              value: 'notValidated'
                            }
                          ],
                          connectorsConnexion,
                          ''
                        ]}
                      />
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Mes pages" xs={3}>
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item justify="flex-start">
                      <ListField
                        values={selectedPlatformPages}
                        onChange={handleChangePlatformPage}
                        actionTxt="Ajouter une page"
                        deleteAction
                        markDelete
                        variant="customFields"
                        inputs={[
                          'TextField',
                          'Select',
                          'Select',
                          'TextField',
                          'Select'
                        ]}
                        labels={[
                          'Nom',
                          'Media',
                          'Connexion',
                          'External ID',
                          'Statut'
                        ]}
                        options={[
                          'name',
                          'media',
                          'connexionId',
                          'externalId',
                          'status'
                        ]}
                        fieldsFormat={[
                          '',
                          'pageActivableMedia',
                          connectorsConnexion,
                          '',
                          [
                            {
                              label: 'Validé',
                              value: 'validated'
                            },
                            {
                              label: 'En cours de validation',
                              value: 'notValidated'
                            }
                          ]
                        ]}
                      />
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1}>
            <Item justify="flex-start">
              <FormSection title="Account Managers" xs={3}>
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item justify="flex-start">
                      <ListField
                        options={adminUsers.map(formatListFieldUser)}
                        searchOptionsKeys={['email']}
                        hideKeys={['_id']}
                        values={selectedAccountManagers}
                        onChange={handleChange('selectedAccountManagers')}
                        actionTxt="Ajouter un account manager"
                        deleteAction
                        markDelete
                        variant="asyncAutoComplete"
                        onChangeInput={value => {
                          pollUsers(value, true);
                        }}
                      />
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
          <Row spacing={1} justify="flex-start">
            <Item justify="flex-start">
              <FormSection title="Mode d'achat DV360" xs={3}>
                <Container>
                  <Row spacing={0} justify="flex-start">
                    <Item xs={6} justify="flex-start">
                      <Item xs={11} justify="flex-start">
                        <Select
                          small
                          required
                          options={budgetTypeImpressionsOptions}
                          value={selectedBudgetTypeImpressions}
                          onChange={value => {
                            handleChange('selectedBudgetTypeImpressions')(
                              value
                            );
                          }}
                        />
                      </Item>
                    </Item>
                  </Row>
                </Container>
              </FormSection>
            </Item>
          </Row>
        </Loading>
      </Container>
    </Modal>
  );
};

ModalForm.defaultProps = {
  onClose: () => null
};

ModalForm.propTypes = {
  onNext: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  config: PropTypes.objectOf(PropTypes.any).isRequired
};

export default ModalForm;
